import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Form } from 'react-form';
import moment from 'moment';
import Datepicker from 'react-datepicker';
import PropTypes, { any } from 'prop-types';
import Sidebar from '../../../../../components/Sidebar';
import { sendPreRegistrationInvites } from '../../../../../actions/event-profile-actions';
import { Select } from '../../../../../components/Form';

import styles from './sidebarInvitePreRegistrants.scss';

const SidebarInvitePreRegistrants = (props) => {
    const {
        eventId,
        onClose,
        registrationStartDate,
        timeZone,
        onSubmitSuccess,
        preRegFilterOptions,
        preRegStats,
    } = props;
    const [categoryId, setCategoryId] = useState(undefined);
    const [selectedMedalTier, setSelectedMedalTier] = useState();
    const [playerIDsValue, setPlayerIDsValue] = useState('');
    const [providerMessage, setProviderMessage] = useState('');
    const [date, setDate] = useState(null);
    const [validationErrors, setValidationErrors] = useState([]);
    const [categoryError, setCategoryError] = useState(false);
    const [playersIDsError, setPlayersIDsError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dispatch = useDispatch();

    const selectedStat = preRegStats?.find(stat => stat.divisionCategoryId === parseInt(categoryId) && stat.medalTier === parseInt(selectedMedalTier));
    const remainingCount = selectedStat?.remainingInvitations;
    const submitButtonLabel = remainingCount ? `Send ${remainingCount} Invite${remainingCount > 1 ? 's' : ''}` : 'Send Invites';

    const medalOptions = preRegStats
        ?.filter(stat => stat.divisionCategoryId === parseInt(categoryId) && stat.remainingInvitations > 0)
        .map(stat => ({
            id: stat.medalTier,
            label: stat.medalTier === 1 ? `Gold (${stat.remainingInvitations})` : stat.medalTier === 2 ? `Silver (${stat.remainingInvitations})` : `Bronze (${stat.remainingInvitations})`,
        }));

    const handleChangeCategory = (category) => {
        setCategoryId(category);
        setCategoryError(false);
        setValidationErrors([]);
    };

    const handleChangeMedalTier = (medalTier) => {
        setSelectedMedalTier(medalTier);
        setCategoryError(false);
        setValidationErrors([]);
    };

    const handlePaste = (event) => {
        event.preventDefault();
        const paste = (event.clipboardData || window.clipboardData).getData('text');
        const numbers = paste
            .split(/\r?\n/)
            .map(line => line.trim())
            .filter(line => /^[0-9]+$/.test(line))
            .join('\n');

        setPlayerIDsValue(prevValue => {
            return prevValue ? `${prevValue}\n${numbers}` : numbers;
        });
    };

    const handleChangePlayerIDs = (event) => {
        setPlayersIDsError(false);
        setValidationErrors([]);
        const input = event.target.value;
        const filteredLines = input
            .split(/\r?\n/)
            .map(line => line.replace(/[^0-9]/g, ''));
        const filteredInput = filteredLines.join('\n');
        setPlayerIDsValue(filteredInput);
    };

    const handleChangeMessage = (event) => {
        setProviderMessage(event.target.value);
    };

    const handleSubmit = async (event) => {
        setIsSubmitting(true);
        event.preventDefault();
        const playerIDs = playerIDsValue.split('\n').filter(line => line.trim() !== '');
        const parsedPlayerIDs = playerIDs.map((playerId) => parseInt(playerId));
        const validationErrors = [];

        if (!playerIDs.length && !selectedMedalTier) {
            setPlayersIDsError(true);
            validationErrors.push('You must either provide player IDs or select a medal tier to proceed.');
        }
        if (playerIDs.length > 1000) {
            validationErrors.push('No more than 1000 players can be submitted at once. Please enter 1000 IDs or less');
        }
        if (!categoryId) {
            setCategoryError(true);
            validationErrors.push('No category was provided. Please select a category');
        }
        if (validationErrors.length > 0) {
            setValidationErrors(validationErrors);
            setIsSubmitting(false);
            return;
        }
        const payload = {
            playerIds: parsedPlayerIDs,
            divisionCategoryId: categoryId,
            medalTier: selectedMedalTier,
            expirationDate: date ? date.format('YYYY-MM-DD') : null,
            message: providerMessage,
        };
        const medalTierLabel = selectedStat?.medalTier === 1 ? 'Gold' : selectedStat?.medalTier === 2 ? 'Silver' : 'Bronze';
        let successMessage = '';
        const selectedCategory = preRegFilterOptions.divisionCategories.find(cat => cat.id === parseInt(categoryId));
        if (selectedMedalTier) {
            successMessage = `You sent an invite to ${remainingCount} pre-registrants for ${selectedCategory?.label} who were ${medalTierLabel} Medal Winners.`;
        } else {
            successMessage += `You sent an invite to ${playerIDs.length} pre-registrants for ${selectedCategory?.label}.`;
        }
        const res = await dispatch(sendPreRegistrationInvites(eventId, payload, successMessage));
        setIsSubmitting(false);
        if (res.success) {
            onClose();
            onSubmitSuccess(res.data);
        }
    };

    const minDatePickerDate = moment().tz(timeZone?.value).startOf('day').add(1, 'day');
    const maxDatePickerDate = moment.utc(registrationStartDate).tz(timeZone?.value).endOf('day').subtract(1, 'day');

    return (
        <Sidebar onClose={onClose}>
            <div className={styles.sidebarContent}>
                <div>
                    <div className="text-highlight2 text-dark text-center mt32 mb25">
                        Invite pre-registrants
                    </div>
                </div>
                <Form>
                    {() => {
                        return (
                            <form onSubmit={handleSubmit} className={styles.sidebarForm}>
                                {validationErrors.length > 0 && (validationErrors.map((error, i) => {
                                    return (<p className={styles.validationError} key={`preRegFormError-${i}`}>{error}</p>);
                                }))}
                                <Select
                                    id="category"
                                    field="category"
                                    options={preRegFilterOptions.divisionCategories}
                                    hiddenLabel={true}
                                    onChange={handleChangeCategory}
                                    themeV3
                                    label="Category"
                                    formGroupCssClass={`bg-white ${categoryError ? styles.selectWithError : ''}`}
                                    emptyOption={`Select a category`}
                                />
                                <Select
                                    id="medalTier"
                                    field="medalTier"
                                    options={medalOptions}
                                    hiddenLabel={true}
                                    disabled={!medalOptions || medalOptions.length === 0 || playerIDsValue.length > 0}
                                    onChange={(selectedMedalTier) => handleChangeMedalTier(selectedMedalTier)}
                                    themeV3
                                    label="Medal Winners"
                                    formGroupCssClass="bg-white mb8"
                                    emptyOption="Select a Medal Type"
                                />
                                <div className="text-small text-muted mt8 mb8">Select a medal type or add player IDs. If any medal types aren't displayed, this group has already been invited.</div>
                                <div className="utr-text text-dark text-left mt24 mb8">INVITEE PLAYER IDS</div>
                                <textarea
                                    value={playerIDsValue}
                                    onChange={handleChangePlayerIDs}
                                    onPaste={handlePaste}
                                    placeholder="Enter or paste player IDs here, one ID per line. Only 1000 allowed per submission."
                                    className={`${styles.playersInput} ${playersIDsError ? styles.playersInputWithError : ''}`}
                                    disabled={!!selectedMedalTier}
                                />
                                <div className={`${styles.datePickerWrapper} mt24`}>
                                    <label className={styles.datePickerLabel}>Invite expires</label>
                                    <Datepicker
                                        className={`form-control date-picker-input clickable bg-white ${styles.datePicker}`}
                                        onChange={(newDate) => {
                                            setDate(moment(newDate));
                                        }}
                                        selected={date}
                                        openToDate={date || minDatePickerDate}
                                        dateFormat="dddd, MMMM D"
                                        maxDate={maxDatePickerDate}
                                        minDate={minDatePickerDate}
                                        placeholderText="Select a date (optional)"
                                    />
                                </div>
                                <div className="utr-text text-dark text-left mt24 mb8">PROVIDER MESSAGE (OPTIONAL)</div>
                                <textarea
                                    value={providerMessage}
                                    onChange={handleChangeMessage}
                                    placeholder="Enter your message here."
                                    className={`${styles.playersInput}`}
                                />
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="btn btn-primary btn-xl btn-block text-uppercase mt24"
                                >
                                    {submitButtonLabel}
                                </button>
                            </form>
                        );
                    }}
                </Form>

            </div>
        </Sidebar>
    );
};

SidebarInvitePreRegistrants.propTypes = {
    eventId: PropTypes.number.isRequired,
    onClose: PropTypes.func,
    registrationStartDate: PropTypes.string,
    timeZone: PropTypes.objectOf(PropTypes.any).isRequired,
    onSubmitSuccess: PropTypes.func,
    preRegFilterOptions: PropTypes.objectOf(any).isRequired,
};

SidebarInvitePreRegistrants.defaultProps = {
    onClose: () => { },
    registrationStartDate: '',
    onSubmitSuccess: () => { },
};

export default withRouter(SidebarInvitePreRegistrants);
